import { FC, type JSX } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { productUnits } from '@eneco/enumerations/productUnits';
import { useFormatter } from '@eneco/i18n';
import { NotificationBox, Stack } from '@eneco/sparky';
import { Fields as OverviewStepFields } from '@sitecore/types/manual/smeFlowExperiment/OverviewStep';
import { Fields as SummaryStepFields } from '@sitecore/types/SMESummaryStep';

import { PriceDetails } from '..';
import { getBilledVolume, getTaxedVolume, sumBilledVolumes } from './billedVolume';
import { PriceDetailsOverviewRedeliveryNotification } from './PriceDetailsOverviewRedeliveryNotification';
import { FlowContextSme } from '../../context';
import {
  checkOfferIncludesDynamicPricingProduct,
  checkOfferIncludesNonDynamicPricingProduct,
  energyTypeIncludes,
  getCashbackTexts,
  getPriceDetailsExplanationTexts,
} from '../../helpers';
import { CostDetails, DetailsType, EnergyType, Offer } from '../../types';
import { NettingDisclosure } from '../NettingDisclosure/NettingDisclosure';
import { isNettingDisclosureAvailable } from '../NettingDisclosure/nettingFigures';
import { NettingSplitFeature } from '../NettingDisclosure/NettingSplitFeature';

export type PriceDetailsOverviewFields = Partial<OverviewStepFields | SummaryStepFields>;

interface Props {
  flowContext: FlowContextSme;
  offer?: Offer;
  textualData?: PriceDetailsOverviewFields;

  costDetails?: CostDetails;
}

export const PriceDetailsOverview: FC<Props> = ({ flowContext, textualData, offer }): JSX.Element => {
  const { energyType = EnergyType.Electricity, hasDoubleMeter, isSME } = flowContext;

  const { costDetails, usages } = offer || {};

  const { electricityNormal = 0, electricityLow = 0, electricityRedeliveryRemainder = 0, gas = 0 } = usages || {};

  const electricityUsageTotal = (electricityNormal ?? 0) + (electricityLow ?? 0);
  const gasUsageTotal = gas ?? 0;

  const { currency, number } = useFormatter();

  const costsType = isSME ? 'vatExcluded' : 'vatIncluded';

  // Each row multiplies its rate by the volume DC charged the row's amount over — after netting, with V3 —
  // rather than by the offer's `usages`, which DC reports separately and which can be 0 for a customer who
  // redelivers more than they consume. `usages` is only the fallback when a cost detail carries no volume.
  const electricityTotal = costDetails?.electricityTotal;
  const billedNormalVolume =
    getBilledVolume(electricityTotal?.yearlyCostsVariableHighWithoutTax) ?? electricityNormal ?? 0;
  const billedLowVolume = getBilledVolume(electricityTotal?.yearlyCostsVariableLowWithoutTax) ?? electricityLow ?? 0;
  const billedSingleMeterVolume =
    getBilledVolume(electricityTotal?.yearlyCostsVariableWithoutTax) ??
    sumBilledVolumes(
      electricityTotal?.yearlyCostsVariableHighWithoutTax,
      electricityTotal?.yearlyCostsVariableLowWithoutTax,
    ) ??
    electricityNormal ??
    0;
  const taxedElectricityVolume = getTaxedVolume(costDetails?.electricityEnergyTaxDetails) ?? electricityUsageTotal;

  const shouldShowDynamicContractNotification =
    checkOfferIncludesDynamicPricingProduct(offer) &&
    !checkOfferIncludesNonDynamicPricingProduct(offer) &&
    !!textualData?.costDetails?.dynamicPricingNotification?.value?.content;

  const shouldShowHybridcContractNotification =
    checkOfferIncludesDynamicPricingProduct(offer) &&
    checkOfferIncludesNonDynamicPricingProduct(offer) &&
    !!textualData?.costDetails?.hybridNotification?.value?.content;

  const shouldShowDynamicPricingLabels =
    checkOfferIncludesDynamicPricingProduct(offer) && electricityRedeliveryRemainder !== 0;

  const shouldShowTaxBracketsPriceComponents = !!(
    costDetails?.electricityEnergyTaxDetails ?? costDetails?.gasEnergyTaxDetails
  );
  const shouldShowLegacyPriceComponents = !shouldShowTaxBracketsPriceComponents;

  // The single redelivery section: DC's current prices, no year labels. Shown whenever the netting split is
  // not — flag off (after the switch-over) or no future cost breakdown.
  const singlePeriodRedeliverySection = Number(electricityRedeliveryRemainder) !== 0 && (
    <PriceDetails.Content
      summary={{
        text: textualData?.electricityCostDetails?.redeliveryLabel?.value,
        value: costDetails?.electricityTotal?.electricityRedeliveryRefund?.[costsType],
      }}
      details={[
        {
          text: `${currency.euroHighPrecision(
            costDetails?.electricityCostDetails?.redeliverytariff?.price?.[costsType],
          )}/${productUnits.electricity}`,
          value:
            textualData?.electricityCostDetails?.[
              shouldShowDynamicPricingLabels ? 'redeliveryDynamicPricingProductTariffLabel' : 'redeliveryTariffLabel'
            ]?.value,
        },
        {
          text: `${currency.euroHighPrecision(
            costDetails?.electricityCostDetails?.redeliverytariff?.price?.[costsType],
          )} × ${number.noFractionDigits(electricityRedeliveryRemainder ?? 0)} ${productUnits.electricity} = `,
          value: costDetails?.electricityTotal?.electricityRedeliveryRefund?.[costsType],
          highlightDivider: true,
          highlightValue: true,
        },
      ]}
    />
  );

  return (
    <Stack direction="column" gap="4">
      <PriceDetailsOverviewRedeliveryNotification
        flowContext={flowContext}
        usageData={usages}
        redeliveryMoreNotificationTextualData={textualData?.electricityCostDetails?.redeliveryMoreNotification}
        redeliveryLessNotificationTextualData={textualData?.electricityCostDetails?.redeliveryLessNotification}
      />

      {shouldShowDynamicContractNotification && (
        <NotificationBox
          isAlert={false}
          variant={textualData?.costDetails?.dynamicPricingNotification?.value?.variant}
          title={textualData?.costDetails?.dynamicPricingNotification?.value?.title}
          text={<RichText html={textualData?.costDetails?.dynamicPricingNotification?.value?.content} />}
        />
      )}

      {shouldShowHybridcContractNotification && (
        <NotificationBox
          isAlert={false}
          variant={textualData?.costDetails?.hybridNotification?.value?.variant}
          title={textualData?.costDetails?.hybridNotification?.value?.title}
          text={<RichText html={textualData?.costDetails?.hybridNotification?.value?.content} />}
        />
      )}

      {costDetails?.cashback && (
        <PriceDetails>
          <PriceDetails.Title type={DetailsType.Cashback}>
            {textualData?.productTypeLabels?.[
              costDetails?.cashback?.type === 'cashBackDirect' ? 'cashbackDirectLabel' : 'cashbackOnYearNoteLabel'
            ]?.value ?? ''}
          </PriceDetails.Title>

          <PriceDetails.Content
            summary={{
              text: getCashbackTexts(costDetails?.cashback?.type, textualData?.promotionTexts)?.label ?? '',
              value: costDetails?.cashback?.value ?? 0 * -1,
            }}
          />

          <PriceDetails.Footer
            summary={{
              label: textualData?.costDetails?.totalCostsLabel?.value,
              value: costDetails?.cashback?.value ?? 0 * -1,
            }}
          />
        </PriceDetails>
      )}

      {energyTypeIncludes(energyType, EnergyType.Electricity) && (
        <PriceDetails>
          <PriceDetails.Title type={DetailsType.Electricity}>
            {textualData?.productTypeLabels?.electricityProductLabel?.value ?? ''}
          </PriceDetails.Title>
          <PriceDetails.Content
            summary={{
              text: `${textualData?.costDetails?.variableCostsLabel?.value}`,
              value: costDetails?.electricityTotal?.yearlyCostsVariableWithoutTax?.[costsType],
            }}
            details={[
              ...(hasDoubleMeter
                ? [
                    {
                      text: textualData?.electricityCostDetails?.tariffLabel?.value,
                      highlightText: true,
                    },
                    {
                      text: `${currency.euroHighPrecision(
                        costDetails?.electricityCostDetails?.tariffWithoutTax?.components?.find(
                          (c: { type: string }) => c.type === 'deliverycosts',
                        )?.[costsType],
                      )}/${productUnits.electricity}`,
                      value:
                        textualData?.electricityCostDetails?.[
                          shouldShowDynamicPricingLabels
                            ? 'tariffDynamicPricingProductDeliveryCostsLabel'
                            : 'tariffDeliveryCostsLabel'
                        ]?.value,
                    },

                    ...(shouldShowLegacyPriceComponents
                      ? [
                          {
                            text: `${currency.euroHighPrecision(
                              costDetails?.electricityCostDetails?.tariffWithoutTax?.components?.find(
                                (c: { type: string }) => c.type === 'energytax',
                              )?.[costsType],
                            )}/${productUnits.electricity}`,
                            value: textualData?.electricityCostDetails?.energyTaxLabel?.value,
                          },
                          {
                            text: `${currency.euroHighPrecision(
                              costDetails?.electricityCostDetails?.tariffWithoutTax?.components?.find(
                                (c: { type: string }) => c.type === 'storagesustainableenergy',
                              )?.[costsType],
                            )}/${productUnits.electricity}`,
                            value: textualData?.electricityCostDetails?.storageSustainableEnergyLabel?.value,
                          },
                        ]
                      : []),

                    {
                      text: `${currency.euroHighPrecision(
                        costDetails?.electricityCostDetails?.tariffWithoutTax?.price?.[costsType],
                      )} × ${number.noFractionDigits(billedNormalVolume)} ${productUnits.electricity} = `,
                      value: costDetails?.electricityTotal?.yearlyCostsVariableHighWithoutTax?.[costsType],
                      highlightDivider: true,
                      highlightValue: true,
                    },
                    {
                      text: textualData?.electricityCostDetails?.lowTarifflLabel?.value,
                      highlightText: true,
                    },
                    {
                      text: `${currency.euroHighPrecision(
                        costDetails?.electricityCostDetails?.lowTariffWithoutTax?.components?.find(
                          (c: { type: string }) => c.type === 'deliverycosts',
                        )?.[costsType],
                      )}/${productUnits.electricity}`,
                      value:
                        textualData?.electricityCostDetails?.[
                          shouldShowDynamicPricingLabels
                            ? 'tariffDynamicPricingProductDeliveryCostsLabel'
                            : 'tariffDeliveryCostsLabel'
                        ]?.value,
                    },
                    ...(shouldShowLegacyPriceComponents
                      ? [
                          {
                            text: `${currency.euroHighPrecision(
                              costDetails?.electricityCostDetails?.lowTariffWithoutTax?.components?.find(
                                (c: { type: string }) => c.type === 'energytax',
                              )?.[costsType],
                            )}/${productUnits.electricity}`,
                            value: textualData?.electricityCostDetails?.energyTaxLabel?.value,
                          },
                          {
                            text: `${currency.euroHighPrecision(
                              costDetails?.electricityCostDetails?.lowTariffWithoutTax?.components?.find(
                                (c: { type: string }) => c.type === 'storagesustainableenergy',
                              )?.[costsType],
                            )}/${productUnits.electricity}`,
                            value: textualData?.electricityCostDetails?.storageSustainableEnergyLabel?.value,
                          },
                        ]
                      : []),

                    {
                      text: `${currency.euroHighPrecision(
                        costDetails?.electricityCostDetails?.lowTariffWithoutTax?.price?.[costsType],
                      )} × ${number.noFractionDigits(billedLowVolume)} ${productUnits.electricity} = `,
                      value: costDetails?.electricityTotal?.yearlyCostsVariableLowWithoutTax?.[costsType],
                      highlightDivider: true,
                      highlightValue: true,
                    },
                  ]
                : [
                    {
                      text: `${currency.euroHighPrecision(
                        costDetails?.electricityCostDetails?.tariffWithoutTax?.components?.find(
                          (c: { type: string }) => c.type === 'deliverycosts',
                        )?.[costsType],
                      )}/${productUnits.electricity}`,
                      value:
                        textualData?.electricityCostDetails?.[
                          shouldShowDynamicPricingLabels
                            ? 'tariffDynamicPricingProductDeliveryCostsLabel'
                            : 'tariffDeliveryCostsLabel'
                        ]?.value,
                    },
                    ...(shouldShowLegacyPriceComponents
                      ? [
                          {
                            text: `${currency.euroHighPrecision(
                              costDetails?.electricityCostDetails?.tariffWithoutTax?.components?.find(
                                (c: { type: string }) => c.type === 'energytax',
                              )?.[costsType],
                            )}/${productUnits.electricity}`,
                            value: textualData?.electricityCostDetails?.energyTaxLabel?.value,
                          },
                          {
                            text: `${currency.euroHighPrecision(
                              costDetails?.electricityCostDetails?.tariffWithoutTax?.components?.find(
                                (c: { type: string }) => c.type === 'storagesustainableenergy',
                              )?.[costsType],
                            )}/${productUnits.electricity}`,
                            value: textualData?.electricityCostDetails?.storageSustainableEnergyLabel?.value,
                          },
                        ]
                      : []),

                    {
                      text: `${currency.euroHighPrecision(
                        costDetails?.electricityCostDetails?.tariffWithoutTax?.price?.[costsType],
                      )} × ${number.noFractionDigits(billedSingleMeterVolume)} ${productUnits.electricity} = `,
                      value: costDetails?.electricityTotal?.yearlyCostsVariableWithoutTax?.[costsType],
                      highlightDivider: true,
                      highlightValue: true,
                    },
                  ]),
            ]}
          />

          {shouldShowTaxBracketsPriceComponents && (
            <PriceDetails.Content
              summary={{
                text: textualData?.electricityCostDetails?.governmentLeviesLabel?.value,
                value: currency.euroHighPrecision(costDetails?.electricityTotal?.yearlyEnergyTax?.[costsType]),
              }}
              details={[
                ...Object.keys(costDetails?.electricityEnergyTaxDetails ?? {}).reduce(
                  (acc, key) => {
                    const bracket = costDetails?.electricityEnergyTaxDetails[key];

                    bracket.active &&
                      acc.push({
                        text: `${currency.euroHighPrecision(bracket.price?.[costsType])}/${productUnits.electricity}`,
                        value: `${number.noFractionDigits(bracket.usageMin ?? 0)} - ${number.noFractionDigits(
                          bracket.usageMax ?? 0,
                        )} ${textualData?.electricityCostDetails?.taxBracket1Label?.value}
                        `,
                      });

                    return acc;
                  },
                  [] as { text: string; value: string }[],
                ),

                {
                  text: `${textualData?.costDetails?.usageLabel?.value}: ${number.noFractionDigits(
                    taxedElectricityVolume,
                  )} ${productUnits.electricity}`,
                  value: currency.euroHighPrecision(costDetails?.electricityTotal?.yearlyEnergyTax?.[costsType]),
                  highlightDivider: true,
                  highlightValue: true,
                },
              ]}
            />
          )}

          <PriceDetails.Content
            summary={{
              text: textualData?.costDetails?.fixedCostsLabel?.value,
              value: costDetails?.electricityTotal?.yearlyCostsFixed?.[costsType],
            }}
            details={[
              {
                text: textualData?.electricityCostDetails?.standingChargeLabel?.value,
                value: costDetails?.electricityCostDetails?.standingcharge?.price?.[costsType],
              },
              {
                text: textualData?.electricityCostDetails?.deliveryCostsLabel?.value,
                value: costDetails?.electricityCostDetails?.deliverycosts?.price?.[costsType],
              },
              {
                text: textualData?.electricityCostDetails?.taxDiscountLabel?.value,
                value: costDetails?.electricityCostDetails?.taxdiscount?.price?.[costsType],
              },
            ]}
          />

          <NettingSplitFeature>
            {isNettingSplitEnabled =>
              isNettingSplitEnabled && isNettingDisclosureAvailable(offer) ? (
                <NettingDisclosure offer={offer} />
              ) : (
                singlePeriodRedeliverySection
              )
            }
          </NettingSplitFeature>

          <PriceDetails.Footer
            summary={{
              label: textualData?.costDetails?.monthlyTermLabel?.value,
              value: costDetails?.electricityTotal?.monthlyCosts?.[costsType],
            }}
            details={[
              {
                text: textualData?.costDetails?.yearlyTermLabel?.value,
                value: costDetails?.electricityTotal?.yearlyCosts?.[costsType],
                highlightDivider: true,
              },
            ]}
          />
        </PriceDetails>
      )}

      {energyTypeIncludes(energyType, EnergyType.Gas) && (
        <PriceDetails>
          <PriceDetails.Title type={DetailsType.Gas}>
            {textualData?.productTypeLabels?.gasProductLabel?.value ?? ''}
          </PriceDetails.Title>
          <PriceDetails.Content
            summary={{
              text: textualData?.costDetails?.variableCostsLabel?.value,
              value: costDetails?.gasTotal?.yearlyCostsVariableWithoutTax?.[costsType],
            }}
            details={[
              {
                text: `${currency.euroHighPrecision(
                  costDetails?.gasCostDetails?.tariffWithoutTax?.components?.find(
                    (c: { type: string }) => c.type === 'deliverycosts',
                  )?.[costsType],
                )}/${productUnits.gas}`,
                value: textualData?.gasCostDetails?.tariffLabel?.value,
              },
              ...(shouldShowLegacyPriceComponents
                ? [
                    {
                      text: `${currency.euroHighPrecision(
                        costDetails?.gasCostDetails?.tariffWithoutTax?.components?.find(
                          (c: { type: string }) => c.type === 'energytax',
                        )?.[costsType],
                      )}/${productUnits.gas}`,
                      value: textualData?.gasCostDetails?.energyTaxLabel?.value,
                    },
                    {
                      text: `${currency.euroHighPrecision(
                        costDetails?.gasCostDetails?.tariffWithoutTax?.components?.find(
                          (c: { type: string }) => c.type === 'storagesustainableenergy',
                        )?.[costsType],
                      )}/${productUnits.gas}`,
                      value: textualData?.gasCostDetails?.storageSustainableEnergyLabel?.value,
                    },
                  ]
                : []),

              {
                text: `${currency.euroHighPrecision(
                  costDetails?.gasCostDetails?.tariffWithoutTax?.price?.[costsType],
                )} × ${number.noFractionDigits(gas ?? 0)} ${productUnits.gas} = `,
                value: costDetails?.gasTotal?.yearlyCostsVariableWithoutTax?.[costsType],
                highlightDivider: true,
                highlightValue: true,
              },
            ]}
          />

          {shouldShowTaxBracketsPriceComponents && (
            <PriceDetails.Content
              summary={{
                text: textualData?.gasCostDetails?.governmentLeviesLabel?.value,
                value: currency.euroHighPrecision(costDetails?.gasTotal?.yearlyEnergyTax?.[costsType]),
              }}
              details={[
                ...Object.keys(costDetails?.gasEnergyTaxDetails ?? {}).reduce(
                  (acc, key) => {
                    const bracket = costDetails?.gasEnergyTaxDetails[key];

                    bracket.active &&
                      acc.push({
                        text: `${currency.euroHighPrecision(bracket.price?.[costsType])}/${productUnits.gas}`,
                        value: `${number.noFractionDigits(bracket.usageMin ?? 0)} - ${number.noFractionDigits(
                          bracket.usageMax ?? 0,
                        )} ${textualData?.gasCostDetails?.taxBracket1Label?.value}
                        `,
                      });

                    return acc;
                  },
                  [] as { text: string; value: string }[],
                ),

                {
                  text: `${textualData?.costDetails?.usageLabel?.value}: ${number.noFractionDigits(gasUsageTotal)} ${
                    productUnits.gas
                  }`,
                  value: currency.euroHighPrecision(costDetails?.gasTotal?.yearlyEnergyTax?.[costsType]),
                  highlightDivider: true,
                  highlightValue: true,
                },
              ]}
            />
          )}

          <PriceDetails.Content
            summary={{
              text: textualData?.costDetails?.fixedCostsLabel?.value,
              value: costDetails?.gasTotal?.yearlyCostsFixed?.[costsType],
            }}
            details={[
              {
                text: textualData?.gasCostDetails?.standingChargeLabel?.value,
                value: costDetails?.gasCostDetails?.standingcharge?.price?.[costsType],
              },
              {
                text: `${textualData?.gasCostDetails?.deliveryCostsLabel?.value ?? ''} (gasmeter <6G, 40m³ per uur)`,
                value: costDetails?.gasCostDetails?.deliverycosts?.price?.[costsType],
              },
            ]}
          />

          <PriceDetails.Footer
            summary={{
              label: textualData?.costDetails?.monthlyTermLabel?.value,
              value: costDetails?.gasTotal?.monthlyCosts?.[costsType],
            }}
            details={[
              {
                text: textualData?.costDetails?.yearlyTermLabel?.value,
                value: costDetails?.gasTotal?.yearlyCosts?.[costsType],
                highlightDivider: true,
              },
            ]}
          />
        </PriceDetails>
      )}

      {energyTypeIncludes(energyType, EnergyType.Warmth) && (
        <PriceDetails>
          <PriceDetails.Title type={DetailsType.Warmth}>
            {textualData?.productTypeLabels?.warmthProductLabel?.value ?? ''}
          </PriceDetails.Title>

          <PriceDetails.Content
            summary={{
              text: textualData?.costDetails?.variableCostsLabel?.value,
              value:
                costDetails?.warmthTotal?.yearlyCostsVariable?.[costsType] ||
                costDetails?.warmthEkvTotal?.yearlyCostsVariable?.[costsType],
            }}
            details={[
              {
                text: `${currency.euroHighPrecision(
                  costDetails?.warmthCostDetails?.tariff?.price?.[costsType] ||
                    costDetails?.warmthEkvCostDetails?.tariff?.price?.[costsType],
                )}/${productUnits.warmth}`,
                value: textualData?.warmthCostDetails?.tariffLabel?.value,
              },
            ]}
          />

          <PriceDetails.Content
            summary={{
              text: textualData?.costDetails?.fixedCostsLabel?.value,
              value:
                costDetails?.warmthTotal?.yearlyCostsFixed?.[costsType] ||
                costDetails?.warmthEkvTotal?.yearlyCostsFixed?.[costsType],
            }}
            details={[
              {
                text: textualData?.warmthCostDetails?.deliveryCostsLabel?.value,
                value:
                  costDetails?.warmthCostDetails?.deliverycosts?.price?.[costsType] ||
                  costDetails?.warmthEkvCostDetails?.deliverycosts?.price?.[costsType],
              },
              {
                text: textualData?.warmthCostDetails?.standingChargeLabel?.value,
                value:
                  costDetails?.warmthCostDetails?.standingcharge?.price?.[costsType] ||
                  costDetails?.warmthEkvCostDetails?.standingcharge?.price?.[costsType],
              },
            ]}
          />

          <PriceDetails.Footer
            summary={{
              label: textualData?.costDetails?.monthlyTermLabel?.value,
              value:
                costDetails?.warmthTotal?.monthlyCosts?.[costsType] ??
                costDetails?.warmthEkvTotal?.monthlyCosts?.[costsType],
            }}
            details={[
              {
                text: textualData?.costDetails?.yearlyTermLabel?.value,
                value:
                  costDetails?.warmthTotal?.yearlyCosts?.[costsType] ??
                  costDetails?.warmthEkvTotal?.yearlyCosts?.[costsType],
                highlightDivider: true,
              },
            ]}
          />
        </PriceDetails>
      )}

      {energyTypeIncludes(energyType, EnergyType.TapWater) && (
        <PriceDetails>
          <PriceDetails.Title type={DetailsType.Water}>
            {textualData?.productTypeLabels?.waterProductLabel?.value ?? ''}
          </PriceDetails.Title>

          <PriceDetails.Content
            summary={{
              text: textualData?.costDetails?.variableCostsLabel?.value,
              value: costDetails?.tapwaterTotal?.yearlyCostsVariable?.[costsType],
            }}
            details={[
              {
                text: `${currency.euroHighPrecision(
                  costDetails?.tapwaterCostDetails?.tariffcoldwater?.price?.[costsType],
                )}/${productUnits.tapWater}`,
                value: textualData?.warmthCostDetails?.tariffColdWaterLabel?.value,
              },
              {
                text: `${currency.euroHighPrecision(
                  costDetails?.tapwaterCostDetails?.taxcoldwater?.price?.[costsType],
                )}/${productUnits.tapWater}`,
                value: textualData?.warmthCostDetails?.taxColdWaterLabel?.value,
              },
              {
                text: `${currency.euroHighPrecision(
                  costDetails?.tapwaterCostDetails?.tariffwarmwater?.price?.[costsType],
                )}/${productUnits.tapWater}`,
                value: textualData?.warmthCostDetails?.tariffWarmWaterLabel?.value,
              },
            ]}
          />
          <PriceDetails.Footer
            summary={{
              label: textualData?.costDetails?.monthlyTermLabel?.value,
              value: costDetails?.tapwaterTotal?.monthlyCosts?.[costsType] ?? 0,
            }}
            details={[
              {
                text: textualData?.costDetails?.yearlyTermLabel?.value,
                value: costDetails?.tapwaterTotal?.yearlyCosts?.[costsType],
                highlightDivider: true,
              },
            ]}
          />
        </PriceDetails>
      )}

      {costDetails?.warmtewisselaarCostDetails?.rentwarmthset?.price && (
        <PriceDetails>
          <PriceDetails.Title type={DetailsType.Warmth}>
            {textualData?.productTypeLabels?.warmthExchangeSetLabel?.value ?? ''}
          </PriceDetails.Title>

          <PriceDetails.Content
            summary={{
              text: textualData?.warmthCostDetails?.rentWarmthSet?.value,
              value: costDetails?.warmtewisselaarCostDetails?.rentwarmthset?.price?.[costsType],
            }}
          />

          <PriceDetails.Footer
            summary={{
              label: textualData?.costDetails?.totalCostsLabel?.value,
              value: costDetails?.warmtewisselaarCostDetails?.rentwarmthset?.price?.[costsType],
            }}
          />
        </PriceDetails>
      )}

      <PriceDetails>
        <PriceDetails.Content
          summary={{ text: textualData?.costDetailsExplanation?.explanationTitle?.value, highlightText: true }}
          text={getPriceDetailsExplanationTexts(flowContext, offer, textualData?.costDetailsExplanation)}
        />
      </PriceDetails>
    </Stack>
  );
};
